import { useEffect, useMemo, useState } from 'react';
import './App.css';

const MYSEKAI_PRESETS = ['P1', 'P2', 'P3'] as const;

type MysekaiPreset = (typeof MYSEKAI_PRESETS)[number];

type PresetData = {
  dialogues: string[];
  fixtures: number[];
  hasInvalidData: boolean;
};

const fixtureStorageKey = (preset: MysekaiPreset) => (
  preset === 'P1' ? 'mysekai-owned-fixtures' : `mysekai-owned-fixtures_${preset}`
);

const dialogueStorageKey = (preset: MysekaiPreset) => (
  preset === 'P1' ? 'mysekai_seenDialogues' : `mysekai_seenDialogues_${preset}`
);

function readStoredJson(key: string): { value: unknown; invalid: boolean } {
  const raw = window.localStorage.getItem(key);
  if (!raw) return { value: null, invalid: false };

  try {
    return { value: JSON.parse(raw), invalid: false };
  } catch {
    return { value: null, invalid: true };
  }
}

function readPresetData(preset: MysekaiPreset): PresetData {
  const storedDialogues = readStoredJson(dialogueStorageKey(preset));
  const storedFixtures = readStoredJson(fixtureStorageKey(preset));

  const dialogueKeys = Array.isArray(storedDialogues.value)
    ? storedDialogues.value.filter((value): value is string => typeof value === 'string')
    : [];

  // 마이세카 탭의 기존 저장 버튼과 동일한 하위 호환 포맷(lua 이름)을 사용합니다.
  const dialogues = dialogueKeys.map((key) => (
    key.includes(':') ? key.split(':')[1] : key
  ));

  const fixtures = (
    storedFixtures.value
    && typeof storedFixtures.value === 'object'
    && !Array.isArray(storedFixtures.value)
  )
    ? Object.entries(storedFixtures.value)
      .filter(([, isOwned]) => Boolean(isOwned))
      .map(([fixtureId]) => Number(fixtureId))
      .filter(Number.isFinite)
    : [];

  return {
    dialogues,
    fixtures,
    hasInvalidData: storedDialogues.invalid || storedFixtures.invalid,
  };
}

function App() {
  const [refreshVersion, setRefreshVersion] = useState(0);
  const [downloadedPreset, setDownloadedPreset] = useState<MysekaiPreset | null>(null);

  useEffect(() => {
    const refresh = () => setRefreshVersion((version) => version + 1);
    window.addEventListener('focus', refresh);
    window.addEventListener('storage', refresh);

    return () => {
      window.removeEventListener('focus', refresh);
      window.removeEventListener('storage', refresh);
    };
  }, []);

  const presetData = useMemo(
    () => Object.fromEntries(
      MYSEKAI_PRESETS.map((preset) => [preset, readPresetData(preset)]),
    ) as Record<MysekaiPreset, PresetData>,
    [refreshVersion],
  );

  const totalDialogues = MYSEKAI_PRESETS.reduce(
    (total, preset) => total + presetData[preset].dialogues.length,
    0,
  );
  const totalFixtures = MYSEKAI_PRESETS.reduce(
    (total, preset) => total + presetData[preset].fixtures.length,
    0,
  );
  const hasInvalidData = MYSEKAI_PRESETS.some((preset) => presetData[preset].hasInvalidData);

  const handleDownload = (preset: MysekaiPreset) => {
    const data = {
      dialogues: presetData[preset].dialogues,
      fixtures: presetData[preset].fixtures,
    };
    const blob = new Blob([JSON.stringify(data, null, 2)], {
      type: 'application/json',
    });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    const date = new Date().toISOString().split('T')[0];

    anchor.href = url;
    anchor.download = `mysekai_checklist_${preset}_${date}.json`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
    setDownloadedPreset(preset);
  };

  return (
    <main className="backup-page">
      <section className="backup-card" aria-labelledby="backup-title">
        <div className="backup-icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" role="img">
            <path d="M12 3v11m0 0 4-4m-4 4-4-4M5 17v2h14v-2" />
          </svg>
        </div>

        <p className="backup-eyebrow">MYSEKAI LOCAL BACKUP</p>
        <h1 id="backup-title">마이세카 잔여 데이터 다운로드</h1>
        <p className="backup-description">
          이 브라우저의 프리셋 1~3에 남아 있는 마이세카 입력 데이터를
          프리셋별 JSON 파일로 각각 내려받습니다.
        </p>

        <div className="backup-summary" aria-label="브라우저에 남은 전체 데이터">
          <div>
            <span>남은 대사</span>
            <strong>{totalDialogues.toLocaleString()}개</strong>
          </div>
          <div>
            <span>남은 가구</span>
            <strong>{totalFixtures.toLocaleString()}개</strong>
          </div>
        </div>

        <div className="preset-breakdown" aria-label="프리셋별 잔여 데이터">
          {MYSEKAI_PRESETS.map((preset, index) => {
            const data = presetData[preset];
            const itemCount = data.dialogues.length + data.fixtures.length;

            return (
              <div key={preset}>
                <span className="preset-copy">
                  <strong>프리셋 {index + 1}</strong>
                  <small>대사 {data.dialogues.length} · 가구 {data.fixtures.length}</small>
                </span>
                <button
                  type="button"
                  onClick={() => handleDownload(preset)}
                  disabled={itemCount === 0}
                >
                  {downloadedPreset === preset ? '다운로드됨' : 'JSON 다운로드'}
                </button>
              </div>
            );
          })}
        </div>

        {hasInvalidData && (
          <p className="backup-warning" role="alert">
            일부 저장 데이터가 손상되어 읽을 수 있는 항목만 표시합니다.
          </p>
        )}

        {downloadedPreset && (
          <p className="download-complete" role="status">
            프리셋 {MYSEKAI_PRESETS.indexOf(downloadedPreset) + 1} JSON 다운로드를 시작했습니다.
          </p>
        )}

      </section>
    </main>
  );
}

export default App;
